import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bookingService, expertService, sessionService } from '../api/services';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Calendar, Clock, Video, Mic, MessageCircle, AlertCircle,
  Loader2, Search, X, Star, ChevronRight, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_TABS   = ['ALL', 'PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'];
const sessionIcon   = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };
const statusStyle   = {
  PENDING:     'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED:   'bg-forest-50 text-forest-700 border-forest-200',
  COMPLETED:   'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED:   'bg-cream-100 text-forest-400 border-cream-200',
  RESCHEDULED: 'bg-purple-50 text-purple-700 border-purple-200',
};

// ── Skeleton ──────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse">
    <div className="flex gap-4">
      <div className="w-10 h-10 bg-cream-200 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-cream-200 rounded w-2/3" />
        <div className="h-3 bg-cream-100 rounded w-1/2" />
        <div className="h-3 bg-cream-100 rounded w-1/3" />
      </div>
    </div>
  </div>
);

// ── Inline Expert Card ────────────────────────────────────────
const InlineExpertCard = ({ expert, onBook }) => {
  const name = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim() || 'Expert';
  const initials = `${expert.user?.firstName?.[0] ?? ''}${expert.user?.lastName?.[0] ?? ''}`;
  const specs = Array.isArray(expert.specializations)
    ? expert.specializations
    : (() => { try { return JSON.parse(expert.specializations || '[]'); } catch { return []; } })();

  return (
    <div className="bg-white border border-cream-200 rounded-2xl p-4 flex items-start gap-3 hover:shadow-sm hover:border-forest-200 transition-all">
      <div className="w-12 h-12 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0 overflow-hidden">
        {expert.user?.avatar
          ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover" />
          : initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="font-sans font-semibold text-forest-900 text-sm truncate">{name}</h4>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {expert.averageRating > 0 && (
                <span className="flex items-center gap-1 text-xs text-forest-500">
                  <Star size={10} className="fill-gold-400 text-gold-400" />
                  {Number(expert.averageRating).toFixed(1)}
                </span>
              )}
              {expert.yearsOfExperience > 0 && (
                <span className="text-xs text-forest-400">{expert.yearsOfExperience}yrs exp</span>
              )}
              {expert.hourlyRate && (
                <span className="text-xs text-forest-500 font-medium">
                  {expert.currency ?? 'GHS'} {expert.hourlyRate}/hr
                </span>
              )}
            </div>
            {specs.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {specs.slice(0, 2).map((s, i) => (
                  <span key={i} className="text-[10px] bg-forest-50 text-forest-600 border border-forest-100 px-2 py-0.5 rounded-full">{s}</span>
                ))}
                {specs.length > 2 && (
                  <span className="text-[10px] text-forest-400">+{specs.length - 2}</span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => onBook(expert)}
            className="btn-primary text-xs py-1.5 px-3 flex-shrink-0 flex items-center gap-1">
            Book <ChevronRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Find Experts Panel ────────────────────────────────────────
const FindExpertsPanel = ({ onClose, onBookExpert }) => {
  const [search, setSearch] = useState('');
  const [query,  setQuery]  = useState('');

  const { data, loading, error } = useApi(
    () => expertService.browse({ search: query, limit: 12, page: 1 }),
    [query],
  );

  const experts = Array.isArray(data)
    ? data
    : Array.isArray(data?.experts) ? data.experts
    : Array.isArray(data?.data)    ? data.data : [];

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery(search.trim());
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-forest-900/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-cream-50 z-50 flex flex-col shadow-2xl"
        style={{ animation: 'slideInRight .25s ease' }}>

        {/* Panel header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-cream-200 flex-shrink-0">
          <div>
            <h3 className="font-serif font-semibold text-forest-900 text-lg">Find an Expert</h3>
            <p className="text-xs text-forest-500 mt-0.5">Browse and book a session without leaving</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-cream-100 text-forest-400 hover:text-forest-700 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-4 py-3 bg-white border-b border-cream-100 flex-shrink-0">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or specialization..."
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
              />
            </div>
            <button type="submit"
              className="px-4 py-2 rounded-xl bg-forest-800 text-cream-50 text-xs font-medium hover:bg-forest-700 transition-colors">
              Search
            </button>
          </form>
        </div>

        {/* Expert list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-forest-600" />
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
              <AlertCircle size={14} /> Could not load experts.
            </div>
          )}
          {!loading && !error && experts.length === 0 && (
            <div className="text-center py-16 text-forest-400">
              <Users size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-serif text-lg text-forest-600">No experts found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}
          {!loading && experts.map(expert => (
            <InlineExpertCard
              key={expert.id}
              expert={expert}
              onBook={onBookExpert}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-white border-t border-cream-100 flex-shrink-0">
          <Link to="/experts"
            className="text-xs text-forest-500 hover:text-forest-800 transition-colors flex items-center justify-center gap-1.5">
            View full experts directory <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </>
  );
};

// ── Booking Card ──────────────────────────────────────────────
const BookingCard = ({ booking, onCancel, onMarkComplete, basePath, isExpert }) => {
  const [completing, setCompleting] = useState(false);
  const Icon = sessionIcon[booking.sessionType] ?? Video;

  const otherPartyName = isExpert
    ? `${booking.clientProfile?.user?.firstName ?? ''} ${booking.clientProfile?.user?.lastName ?? ''}`.trim() || 'Client'
    : `${booking.expertProfile?.user?.firstName ?? ''} ${booking.expertProfile?.user?.lastName ?? ''}`.trim() || 'Expert';

  const date = booking.scheduledAt
    ? new Date(booking.scheduledAt).toLocaleString('en-GH', { dateStyle: 'long', timeStyle: 'short' })
    : '—';

  const isCancellable  = ['PENDING', 'CONFIRMED'].includes(booking.status);
  const hasSession     = !!booking.session?.id;
  const isConfirmed    = booking.status === 'CONFIRMED';
  const isCompleted    = booking.status === 'COMPLETED';

  const scheduledAt    = booking.scheduledAt ? new Date(booking.scheduledAt) : null;
  const duration       = booking.duration || 60;
  const sessionEndTime = scheduledAt ? new Date(scheduledAt.getTime() + duration * 60 * 1000) : null;
  const sessionPast    = sessionEndTime ? new Date() > sessionEndTime : false;

  const handleComplete = async () => {
    if (!window.confirm('Mark this session as complete?')) return;
    setCompleting(true);
    try { await onMarkComplete(booking.session.id); }
    finally { setCompleting(false); }
  };

  return (
    <div className="bg-white border border-cream-200 rounded-2xl p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon size={17} className="text-forest-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h4 className="font-sans font-semibold text-forest-900 text-sm">
                {booking.sessionType} with {otherPartyName}
              </h4>
              <div className="flex items-center gap-3 mt-1 text-xs text-forest-500">
                <span className="flex items-center gap-1"><Calendar size={10} /> {date}</span>
                <span className="flex items-center gap-1"><Clock size={10} /> {booking.duration} mins</span>
              </div>
              {booking.level?.name && (
                <div className="text-xs text-forest-400 mt-1">{booking.level.name}</div>
              )}
            </div>
            <span className={`text-[10px] font-medium border px-2.5 py-1 rounded-full flex-shrink-0 ${statusStyle[booking.status] ?? ''}`}>
              {booking.status}
            </span>
          </div>
          {booking.notes && (
            <p className="text-xs text-forest-400 mt-2 italic line-clamp-2">"{booking.notes}"</p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100 flex-wrap">
        <Link to={`${basePath}/${booking.id}`}
          className="text-xs font-medium text-forest-600 border border-cream-200 hover:border-forest-400 px-3 py-1.5 rounded-full transition-all">
          View Details →
        </Link>
        {hasSession && isConfirmed && (
          <Link to={`/sessions/${booking.session.id}`} className="btn-primary text-xs py-1.5 px-4">
            Join Session →
          </Link>
        )}
        {hasSession && isConfirmed && sessionPast && (
          <button onClick={handleComplete} disabled={completing}
            className="flex items-center gap-1.5 text-xs border border-forest-200 text-forest-700 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors font-medium disabled:opacity-60">
            {completing ? <Loader2 size={12} className="animate-spin" /> : '✓'}
            Mark Complete
          </button>
        )}
        {isCompleted && hasSession && (
          <Link
            to={isExpert
              ? `/expert/sessions/${booking.session.id}/recording`
              : `/sessions/${booking.session.id}/recording`}
            className="flex items-center gap-1.5 text-xs border border-blue-200 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-full transition-colors font-medium">
            🎬 {booking.session?.recordingUrl ? 'Watch Recording' : 'View Session'}
          </Link>
        )}
        {isCancellable && !isExpert && (
          <button onClick={() => onCancel(booking.id)}
            className="text-xs px-4 py-1.5 rounded-full border border-rust-200 text-rust-500 hover:bg-rust-500 hover:text-white hover:border-rust-500 transition-all font-medium">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const BookingsPage = () => {
  const { isExpert }  = useAuth();
  const navigate      = useNavigate();
  const [activeTab,   setActiveTab]   = useState('ALL');
  const [showExperts, setShowExperts] = useState(false);

  const params = { page: 1, limit: 20 };
  if (activeTab !== 'ALL') params.status = activeTab;

  const { data, loading, error, refetch } = useApi(
    () => isExpert ? expertService.getMyBookings(params) : bookingService.getAll(params),
    [activeTab, isExpert],
  );

  const bookings = Array.isArray(data) ? data
    : Array.isArray(data?.bookings) ? data.bookings
    : Array.isArray(data?.data)     ? data.data : [];

  const basePath = isExpert ? '/expert/bookings' : '/bookings';

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await bookingService.cancel(id, 'Cancelled by client');
      toast.success('Booking cancelled');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not cancel booking');
    }
  };

  const handleMarkComplete = async (sessionId) => {
    try {
      await sessionService.markComplete(sessionId);
      toast.success('Session marked as complete!');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not mark complete');
    }
  };

  // When user clicks Book on an expert inside the panel
  const handleBookExpert = (expert) => {
    setShowExperts(false);
    // Use navigate (no full page reload — avoids triggering assessment redirect)
    navigate(`/experts/${expert.id}`);
  };

  return (
    <DashboardLayout title="My Bookings">

      {/* Slide-in experts panel */}
      {showExperts && (
        <FindExpertsPanel
          onClose={() => setShowExperts(false)}
          onBookExpert={handleBookExpert}
        />
      )}

      {/* Slide animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>

      <div className="max-w-3xl space-y-6">

        {/* Header row — tabs + Find Expert button */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {STATUS_TABS.map((t) => (
              <button key={t} onClick={() => setActiveTab(t)}
                className={`text-xs font-medium px-4 py-2 rounded-full border transition-all ${
                  activeTab === t
                    ? 'bg-forest-800 text-cream-50 border-forest-800'
                    : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'
                }`}>
                {t}
              </button>
            ))}
          </div>

          {/* Find Expert button — only for clients */}
          {!isExpert && (
            <button
              onClick={() => setShowExperts(true)}
              className="btn-primary flex items-center gap-2 text-sm flex-shrink-0">
              {/* <Search size={14} /> Find Expert */}
              <Search size={14} /> Book an Expert
            </button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} className="flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Could not load bookings</p>
              <p className="text-xs mt-0.5 text-rust-400">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && bookings.length === 0 && (
          <div className="text-center py-20 text-forest-400 bg-white rounded-2xl border border-cream-200">
            <Calendar size={40} className="mx-auto mb-4 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No bookings yet</p>
            <p className="text-sm mt-2 mb-6">
              {isExpert
                ? 'Booking requests from clients will appear here.'
                : 'Book a session with one of our tourism experts.'}
            </p>
            {!isExpert && (
              <button onClick={() => setShowExperts(true)} className="btn-primary inline-flex items-center gap-2">
                <Search size={14} /> Find an Expert
              </button>
            )}
          </div>
        )}

        <div className="space-y-4">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : bookings.map((b) => (
                <BookingCard
                  key={b.id}
                  booking={b}
                  onCancel={handleCancel}
                  onMarkComplete={handleMarkComplete}
                  basePath={basePath}
                  isExpert={isExpert}
                />
              ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BookingsPage;